import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import { changeSeconds } from '../../../app/utils';
import IcHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import { setNodeData } from '../../../features/mainSlice';
import Dropdown from '../../shared/Dropdown';
import Tooltip from '../../shared/Tooltip';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 4,
      },
    },
    block: {
      display: 'flex',
      alignItems: 'center',
    },
    option: {
      marginRight: 36,
    },
    radioInput: {
      fontSize: 15,
    },
    radio: {
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$13,
    },
    day: {
      width: 80,
      marginRight: 8,
    },
    dropdown: {
      width: 80,
      margin: '0 8px',
    },
    wait: {
      display: 'flex',
      flexWrap: 'wrap',
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        fontSize: '15px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: '1',
        letterSpacing: 'normal',
        color: color.$13,
        marginBottom: 8,
      },
    },
    tooltipPopper: {
      zIndex: 5000,
    },
  },
  {
    name: 'Wait',
  },
);

// 0 ~ maxNum
const generateDateItem = (maxNum, span = 1) => {
  return Array.from(Array(maxNum / span + 1)).map((v, index) => ({
    text: (index * span).toString(),
    value: index * span,
  }));
};

const dayItem = generateDateItem(30);

const hourItem = generateDateItem(24);

const minuteItem = generateDateItem(60, 5);

const secondItem = generateDateItem(60, 1);

const monthItem = generateDateItem(12);

const yearItem = generateDateItem(2);

const Wait = ({ alwaysOn }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [radioType, setRadioType] = useState('off');
  const [year, setYear] = useState(0);
  const [month, setMonth] = useState(0);
  const [day, setDay] = useState(0);
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);
  const [second, setSecond] = useState(0);
  const { selectedNode } = useSelector(state => state.ma);
  const { waitTime } = selectedNode?.data;

  const onChange = value => {
    if (value === 'on') {
      dispatch(
        setNodeData({
          waitTime: 86400,
        }),
      );
    } else {
      dispatch(
        setNodeData({
          waitTime: null,
        }),
      );
    }
  };

  const onYearChange = value => {
    dispatch(
      setNodeData({
        waitTime:
          second +
          (minute +
            hour * 60 +
            day * 24 * 60 +
            month * 30 * 24 * 60 +
            parseInt(value || 0, 10) * 12 * 30 * 24 * 60) *
            60,
      }),
    );
  };

  const onMonthChange = value => {
    dispatch(
      setNodeData({
        waitTime:
          second +
          (minute +
            hour * 60 +
            day * 24 * 60 +
            parseInt(value || 0, 10) * 30 * 24 * 60 +
            year * 12 * 30 * 24 * 60) *
            60,
      }),
    );
  };

  const onDayChange = value => {
    dispatch(
      setNodeData({
        waitTime:
          second +
          (minute +
            hour * 60 +
            parseInt(value || 0, 10) * 24 * 60 +
            month * 30 * 24 * 60 +
            year * 12 * 30 * 24 * 60) *
            60,
      }),
    );
  };

  const onHourChange = value => {
    dispatch(
      setNodeData({
        waitTime:
          second +
          (minute +
            parseInt(value || 0, 10) * 60 +
            day * 24 * 60 +
            month * 30 * 24 * 60 +
            year * 12 * 30 * 24 * 60) *
            60,
      }),
    );
  };

  const onMinuteChange = value => {
    dispatch(
      setNodeData({
        waitTime:
          second +
          (parseInt(value || 0, 10) +
            hour * 60 +
            day * 24 * 60 +
            month * 30 * 24 * 60 +
            year * 12 * 30 * 24 * 60) *
            60,
      }),
    );
  };

  const onSecondChange = value => {
    dispatch(
      setNodeData({
        waitTime:
          parseInt(value || 0, 10) +
          (minute +
            hour * 60 +
            day * 24 * 60 +
            month * 30 * 24 * 60 +
            year * 12 * 30 * 24 * 60) *
            60,
      }),
    );
  };

  useEffect(() => {
    setRadioType(waitTime === null ? 'off' : 'on');
    if (waitTime !== null) {
      setYear(changeSeconds(waitTime, 'year'));
      setMonth(changeSeconds(waitTime, 'month'));
      setDay(changeSeconds(waitTime, 'day'));
      setHour(changeSeconds(waitTime, 'hour'));
      setMinute(changeSeconds(waitTime, 'minute'));
      setSecond(changeSeconds(waitTime, 'second'));
    }
  }, [waitTime]);

  // 最多上限 2 年
  useEffect(() => {
    if (waitTime > 62208000) {
      dispatch(
        setNodeData({
          waitTime: 62208000,
        }),
      );
    }
  }, [waitTime]);

  const waitComponent = (
    <div className={classes.wait}>
      <div>
        <Dropdown
          className={classes.day}
          menuItems={yearItem}
          value={year}
          onChange={onYearChange}
          disabled={waitTime === null}
        />
        {t('messages.year')}
        <Dropdown
          className={classes.dropdown}
          menuItems={monthItem}
          value={month}
          onChange={onMonthChange}
          disabled={waitTime === null}
        />
        {t('messages.month')}
        <Dropdown
          className={classes.dropdown}
          menuItems={dayItem}
          value={day}
          onChange={onDayChange}
          disabled={waitTime === null}
        />
        {t('messages.day')}
      </div>
      <div>
        <Dropdown
          className={classes.day}
          menuItems={hourItem}
          value={hour}
          onChange={onHourChange}
          disabled={waitTime === null}
        />
        {t('messages.hour')}
        <Dropdown
          className={classes.dropdown}
          menuItems={minuteItem}
          value={minute}
          onChange={onMinuteChange}
          disabled={waitTime === null}
        />
        {t('messages.minute')}
        <Dropdown
          className={classes.dropdown}
          menuItems={secondItem}
          value={second}
          onChange={onSecondChange}
          disabled={waitTime === null}
        />
        {t('messages.second')}
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        {t('messages.wait-title')}
        <Tooltip
          placement="top"
          light
          title={t('messages.waitTooltip')}
          detailClasses={{
            popper: classes.tooltipPopper,
          }}
        >
          <img src={IcHelpCircle} alt="wait title" />
        </Tooltip>
      </div>
      {alwaysOn ? (
        waitComponent
      ) : (
        <FormControl component="fieldset">
          <RadioGroup
            aria-label="default-display"
            name="default-display"
            value={radioType}
            onChange={e => onChange(e?.target?.value)}
          >
            <FormControlLabel
              className={classes.option}
              classes={{
                label: classes.radioInput,
              }}
              value="on"
              control={<Radio color="primary" />}
              label={<div className={classes.radio}>{t('messages.wait')}</div>}
            />
            {waitComponent}
            <FormControlLabel
              className={classes.option}
              classes={{
                label: classes.radioInput,
              }}
              value="off"
              control={<Radio color="primary" />}
              label={
                <div className={classes.radio}>{t('messages.not-wait')}</div>
              }
            />
          </RadioGroup>
        </FormControl>
      )}
    </div>
  );
};

export default Wait;
